import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Calendar } from './ui/calendar';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { CalendarIcon, Clock, Loader2, Check, Trash2, Bell, Mail, MessageSquare, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const ReminderDialog = ({ open, onOpenChange, application }) => {
  const { api } = useAuth();
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [date, setDate] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const defaultPhone = application?.student_phone || application?.student_phone_2 || '';
  const [formData, setFormData] = useState({
    title: '',
    notes: '',
    reminder_time: '09:00',
    send_email: false,
    send_sms: false,
    send_whatsapp: false,
    whatsapp_phone: defaultPhone,
    whatsapp_message: '',
  });

  useEffect(() => {
    if (open && application?.id) {
      fetchReminders();
      // Reset phone to current application's phone each time dialog opens
      setFormData(prev => ({
        ...prev,
        whatsapp_phone: application?.student_phone || application?.student_phone_2 || ''
      }));
    }
  }, [open, application?.id]);

  const fetchReminders = async () => {
    setLoading(true);
    try {
      const response = await api().get(`/reminders?application_id=${application.id}`);
      setReminders(response.data.reminders);
    } catch (error) {
      toast.error('Failed to load reminders');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!date) {
      toast.error('Please select a date');
      return;
    }
    if (!formData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    setSaving(true);
    try {
      // Compute the absolute UTC firing time from the user's local date+time
      const [hh, mm] = (formData.reminder_time || '00:00').split(':').map(n => parseInt(n, 10) || 0);
      const fireAt = new Date(date);
      fireAt.setHours(hh, mm, 0, 0);
      const scheduledAtUtc = fireAt.toISOString();

      await api().post('/reminders', {
        application_id: application?.id,
        title: formData.title,
        notes: formData.notes || null,
        reminder_date: format(date, 'yyyy-MM-dd'),
        reminder_time: formData.reminder_time,
        scheduled_at: scheduledAtUtc,
        send_email: formData.send_email,
        send_sms: formData.send_sms,
        send_whatsapp: formData.send_whatsapp,
        whatsapp_phone: formData.send_whatsapp ? (formData.whatsapp_phone || '').trim() : null,
        whatsapp_message: formData.send_whatsapp ? (formData.whatsapp_message || '').trim() : null,
      });
      const channels = [];
      if (formData.send_email) channels.push('Email');
      if (formData.send_sms) channels.push('SMS');
      if (formData.send_whatsapp) channels.push('WhatsApp');
      toast.success(channels.length
        ? `Reminder set — will auto-send via ${channels.join(', ')}`
        : 'Reminder set successfully');
      setFormData({
        title: '', notes: '', reminder_time: '09:00',
        send_email: false, send_sms: false, send_whatsapp: false,
        whatsapp_phone: application?.student_phone || application?.student_phone_2 || '',
        whatsapp_message: '',
      });
      setDate(null);
      fetchReminders();
    } catch (error) {
      toast.error('Failed to create reminder');
    } finally {
      setSaving(false);
    }
  };

  const toggleComplete = async (reminder) => {
    try {
      await api().put(`/reminders/${reminder.id}`, {
        is_completed: !reminder.is_completed
      });
      setReminders(prev => 
        prev.map(r => r.id === reminder.id ? { ...r, is_completed: !r.is_completed } : r)
      );
      toast.success(reminder.is_completed ? 'Reminder marked as pending' : 'Reminder completed');
    } catch (error) {
      toast.error('Failed to update reminder');
    }
  };

  const deleteReminder = async (reminderId) => {
    try {
      await api().delete(`/reminders/${reminderId}`);
      setReminders(prev => prev.filter(r => r.id !== reminderId));
      toast.success('Reminder deleted');
    } catch (error) {
      toast.error('Failed to delete reminder');
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const reminderDate = new Date(dateStr);
    reminderDate.setHours(0, 0, 0, 0);

    if (reminderDate.getTime() === today.getTime()) return 'Today';
    if (reminderDate.getTime() === tomorrow.getTime()) return 'Tomorrow';
    return format(new Date(dateStr), 'MMM d, yyyy');
  };

  const isPastDue = (dateStr, timeStr) => {
    const now = new Date();
    const reminderDateTime = new Date(`${dateStr}T${timeStr}`);
    return reminderDateTime < now;
  };

  const renderReminders = () => {
    const items = [];
    for (let i = 0; i < reminders.length; i++) {
      const reminder = reminders[i];
      const overdue = isPastDue(reminder.reminder_date, reminder.reminder_time) && !reminder.is_completed;
      items.push(
        <div 
          key={reminder.id}
          className={`p-3 rounded-lg border ${reminder.is_completed ? 'bg-muted/50 opacity-60' : overdue ? 'border-destructive bg-destructive/5' : 'border-border'}`}
          data-testid={`reminder-${reminder.id}`}
        >
          <div className="flex items-start gap-3">
            <Button
              variant="ghost"
              size="icon"
              className={`h-6 w-6 rounded-full flex-shrink-0 ${reminder.is_completed ? 'bg-green-100 text-green-600' : 'border'}`}
              onClick={() => toggleComplete(reminder)}
            >
              {reminder.is_completed && <Check className="w-3 h-3" />}
            </Button>
            <div className="flex-1 min-w-0">
              <p className={`font-medium text-sm ${reminder.is_completed ? 'line-through text-muted-foreground' : ''}`}>
                {reminder.title}
              </p>
              {reminder.notes && (
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{reminder.notes}</p>
              )}
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <Badge variant={overdue ? "destructive" : "outline"} className="text-xs">
                  <CalendarIcon className="w-3 h-3 mr-1" />
                  {formatDate(reminder.reminder_date)}
                </Badge>
                <Badge variant="outline" className="text-xs">
                  <Clock className="w-3 h-3 mr-1" />
                  {reminder.reminder_time}
                </Badge>
                {reminder.send_whatsapp && (
                  <Badge variant="outline" className="text-xs bg-green-50 border-green-300 text-green-700" title={reminder.whatsapp_dispatched ? 'WhatsApp queued' : 'WhatsApp scheduled'}>
                    <MessageCircle className="w-3 h-3 mr-1" />
                    {reminder.whatsapp_dispatched ? 'WA sent' : 'WA'}
                  </Badge>
                )}
                {reminder.send_email && (
                  <Badge variant="outline" className="text-xs">
                    <Mail className="w-3 h-3 mr-1" />
                    Email
                  </Badge>
                )}
                {reminder.send_sms && (
                  <Badge variant="outline" className="text-xs">
                    <MessageSquare className="w-3 h-3 mr-1" />
                    SMS
                  </Badge>
                )}
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-muted-foreground hover:text-destructive"
              onClick={() => deleteReminder(reminder.id)}
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>
      );
    }
    return items;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Reminders
          </DialogTitle>
          <DialogDescription>
            {application?.student_name ? `Set reminders for ${application.student_name}` : 'Manage your reminders'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 overflow-y-auto pr-1 flex-1">
          {/* Create New Reminder Form */}
          <form onSubmit={handleSubmit} className="space-y-3 p-3 bg-muted/30 rounded-lg">
            <div>
              <Label htmlFor="title" className="text-xs">Title *</Label>
              <Input
                id="title"
                placeholder="e.g., Follow up with student"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="h-9"
                data-testid="reminder-title-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Date *</Label>
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal h-9"
                      data-testid="reminder-date-btn"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {date ? format(date, 'MMM d, yyyy') : 'Pick date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={(d) => { setDate(d); setCalendarOpen(false); }}
                      disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <Label htmlFor="time" className="text-xs">Time</Label>
                <Input
                  id="time"
                  type="time"
                  value={formData.reminder_time}
                  onChange={(e) => setFormData(prev => ({ ...prev, reminder_time: e.target.value }))}
                  className="h-9"
                  data-testid="reminder-time-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="notes" className="text-xs">Notes</Label>
              <Textarea
                id="notes"
                placeholder="Additional notes..."
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
                className="resize-none"
                data-testid="reminder-notes-input"
              />
            </div>

            {/* Notification channels */}
            <div className="space-y-2 pt-1 border-t">
              <Label className="text-xs font-medium text-muted-foreground">Auto-send on due date</Label>
              <div className="grid grid-cols-3 gap-2">
                <label className="flex items-center gap-1.5 text-xs cursor-pointer p-1.5 rounded border hover:bg-muted/50" data-testid="reminder-channel-email-label">
                  <Checkbox
                    checked={formData.send_email}
                    onCheckedChange={(c) => setFormData(prev => ({ ...prev, send_email: !!c }))}
                    data-testid="reminder-send-email-checkbox"
                  />
                  <Mail className="w-3 h-3" />
                  Email
                </label>
                <label className="flex items-center gap-1.5 text-xs cursor-pointer p-1.5 rounded border hover:bg-muted/50" data-testid="reminder-channel-sms-label">
                  <Checkbox
                    checked={formData.send_sms}
                    onCheckedChange={(c) => setFormData(prev => ({ ...prev, send_sms: !!c }))}
                    data-testid="reminder-send-sms-checkbox"
                  />
                  <MessageSquare className="w-3 h-3" />
                  SMS
                </label>
                <label className="flex items-center gap-1.5 text-xs cursor-pointer p-1.5 rounded border hover:bg-muted/50 data-[checked=true]:bg-green-50 data-[checked=true]:border-green-400"
                  data-checked={formData.send_whatsapp}
                  data-testid="reminder-channel-whatsapp-label">
                  <Checkbox
                    checked={formData.send_whatsapp}
                    onCheckedChange={(c) => setFormData(prev => ({ ...prev, send_whatsapp: !!c }))}
                    data-testid="reminder-send-whatsapp-checkbox"
                  />
                  <MessageCircle className="w-3 h-3 text-green-600" />
                  WhatsApp
                </label>
              </div>

              {formData.send_whatsapp && (
                <div className="space-y-2 p-2 rounded-md bg-green-50 dark:bg-green-900/20 border border-green-200">
                  <div>
                    <Label className="text-[11px] text-green-800">WhatsApp Phone (with country code)</Label>
                    <Input
                      placeholder="+923001234567"
                      value={formData.whatsapp_phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, whatsapp_phone: e.target.value }))}
                      className="h-8 text-xs"
                      data-testid="reminder-whatsapp-phone-input"
                    />
                  </div>
                  <div>
                    <Label className="text-[11px] text-green-800">Custom message (optional)</Label>
                    <Textarea
                      placeholder="Leave blank to use title + notes"
                      value={formData.whatsapp_message}
                      onChange={(e) => setFormData(prev => ({ ...prev, whatsapp_message: e.target.value }))}
                      rows={2}
                      className="resize-none text-xs"
                      data-testid="reminder-whatsapp-message-input"
                    />
                  </div>
                  <p className="text-[10px] text-green-700">
                    Will be queued automatically on the reminder date/time via your connected WhatsApp extension.
                  </p>
                </div>
              )}
              {(formData.send_email || formData.send_sms) && !formData.send_whatsapp && (
                <p className="text-[10px] text-amber-600">
                  Note: Email/SMS delivery requires an integration to be configured.
                </p>
              )}
            </div>

            <Button type="submit" className="w-full h-9" disabled={saving} data-testid="create-reminder-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Bell className="w-4 h-4 mr-2" />}
              Set Reminder
            </Button>
          </form>

          {/* Existing Reminders */}
          {reminders.length > 0 && (
            <div>
              <h4 className="text-sm font-medium mb-2">Your Reminders ({reminders.length})</h4>
              <div className="space-y-2">
                {loading ? (
                  <div className="text-center py-4 text-muted-foreground">Loading...</div>
                ) : (
                  renderReminders()
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="shrink-0 pt-2 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReminderDialog;
